/**
 * RelayLeaderboard
 *
 * Full relay-mode leaderboard with:
 * - Rank, team name, effective distance, completion %, current city
 * - Champion / Finisher / At-risk eligibility badges
 * - Click to expand member stats inline
 *
 * Props:
 *   campaignId  - string | number
 */
import { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { getCampaign, getCampaignRelayLeaderboard, getCampaignTeamJourney } from '../../api/challenges';
import { resolveCheckpoints } from '../../api/checkpointResolution';
import { normalizeCheckpoint } from '../../api/mapJourney';
import { demoCheckpoints } from '../../api/mockData';
import RelayProgressCard from './RelayProgressCard';
import RelayTeamsMap, { DEFAULT_WORLD_ROUTE, getTeamColor } from '../map/RelayTeamsMap';

function EligibilityBadge({ isChampion, isFinisher }) {
  if (isFinisher) {
    return <span style={pill('#f6ffed', '#52c41a', '#b7eb8f')}>🏅 Finisher</span>;
  }
  if (!isChampion) {
    return <span style={pill('#fff1f0', '#cf1322', '#ffa39e')}>⚠️ Dropout</span>;
  }
  return <span style={pill('#e6f4ff', '#1677ff', '#91caff')}>🏆 Champion</span>;
}

function pill(bg, color, border) {
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 500,
    background: bg,
    color,
    border: `1px solid ${border}`,
  };
}

function MiniBar({ pct, finisher }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 120 }}>
      <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
        <div
          style={{
            width: `${Math.min(100, pct)}%`,
            height: '100%',
            background: finisher ? '#52c41a' : '#1677ff',
            borderRadius: 3,
            transition: 'width 0.3s',
          }}
        />
      </div>
      <span style={{ fontSize: 12, color: '#595959', minWidth: 36 }}>{pct?.toFixed(1)}%</span>
    </div>
  );
}

export default function RelayLeaderboard({ campaignId }) {
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Map controlled state ──────────────────────────────────────────────────
  // Which teams are visible on the shared map (Set<team_id>)
  const [mapVisible, setMapVisible]   = useState(() => new Set());
  // Which team is highlighted (expanded row) — its line gets bold, others dim
  const [highlightedId, setHighlightedId] = useState(null);

  // ── Expanded detail state ─────────────────────────────────────────────────
  const [expandedJourneys, setExpandedJourneys] = useState({}); // { [teamId]: journey | 'loading' | 'error' }

  // ── Route for the map — lazily upgraded from first journey API call ───────
  const [mapRoute, setMapRoute] = useState(DEFAULT_WORLD_ROUTE);
  const routeFetched = useRef(false);

  // ── Campaign checkpoints (real lat/lng from BE) ───────────────────────────
  // Normalized checkpoint objects: { name, lat, lng, distanceKm, emoji?, ... }
  // Used to enrich RelayTeamsMap with real coordinates instead of hardcoded CITY_COORDS.
  const [campaignCheckpoints, setCampaignCheckpoints] = useState(null);

  // Initialise mapVisible once rows load (all teams visible by default)
  const prevRowIds = useRef('');
  useEffect(() => {
    const ids = rows.map(r => r.team_id).join(',');
    if (ids !== prevRowIds.current) {
      prevRowIds.current = ids;
      setMapVisible(new Set(rows.map(r => r.team_id)));
    }
  }, [rows]);

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      try {
        const data = await getCampaignRelayLeaderboard(campaignId);
        if (!ignore) {
          setRows(data);
          // Silently fetch route from top-ranked team for real city coords
          if (!routeFetched.current && data.length > 0) {
            routeFetched.current = true;
            getCampaignTeamJourney(campaignId, data[0].team_id)
              .then((j) => { if (!ignore && j?.route?.length >= 2) setMapRoute(j.route); })
              .catch(() => {});
          }
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, [campaignId]);

  // ── Fetch campaign checkpoint data (lat/lng) for the map ─────────────────
  // Mirrors the pattern used by CampaignRelayMap: getCampaign → resolveCheckpoints → normalize.
  // Falls back to demoCheckpoints (Vietnam domestic) when the campaign has none configured.
  useEffect(() => {
    let ignore = false;
    getCampaign(campaignId)
      .then((campaign) => {
        if (ignore) return;
        const raw = resolveCheckpoints(campaign, demoCheckpoints);
        setCampaignCheckpoints(raw.map(normalizeCheckpoint));
      })
      .catch(() => {
        if (!ignore) setCampaignCheckpoints(demoCheckpoints.map(normalizeCheckpoint));
      });
    return () => { ignore = true; };
  }, [campaignId]);

  /**
   * Toggle a team on the shared map (called from both checkbox strip and row click).
   */
  function toggleMapTeam(teamId) {
    setMapVisible(prev => {
      const next = new Set(prev);
      next.has(teamId) ? next.delete(teamId) : next.add(teamId);
      return next;
    });
  }

  /**
   * Click a leaderboard row:
   *  1. Ensure the team is visible on the map (make it checked if it wasn't)
   *  2. Highlight it on the map (bold line, others dim)
   *  3. Expand / collapse the detail panel
   *  4. Lazy-load the journey detail if not yet fetched
   */
  async function handleRowClick(teamId) {
    const isExpanded = highlightedId === teamId;

    if (isExpanded) {
      // Collapse: remove highlight, keep visibility as-is
      setHighlightedId(null);
      return;
    }

    // Ensure visible on map
    setMapVisible(prev => {
      if (prev.has(teamId)) return prev;
      const next = new Set(prev);
      next.add(teamId);
      return next;
    });

    setHighlightedId(teamId);

    // Lazy-load journey detail if not already cached
    if (!expandedJourneys[teamId]) {
      setExpandedJourneys(prev => ({ ...prev, [teamId]: 'loading' }));
      try {
        const journey = await getCampaignTeamJourney(campaignId, teamId);
        setExpandedJourneys(prev => ({ ...prev, [teamId]: journey }));
        if (journey?.route?.length >= 2) setMapRoute(journey.route);
      } catch {
        setExpandedJourneys(prev => ({ ...prev, [teamId]: 'error' }));
      }
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#8c8c8c', fontSize: 14 }}>
        Chưa có dữ liệu relay. Hệ thống sẽ cập nhật vào lúc 03:00 hàng ngày.
      </div>
    );
  }

  return (
    <div>
      {/* ── Shared world map (all teams) ──────────────────────────────────── */}
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,.05)',
          border: '1px solid #f0f0f0',
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12, color: '#262626' }}>
          🗺️ Bản đồ hành trình các team
        </div>
        <RelayTeamsMap
          teams={rows}
          route={mapRoute}
          checkpoints={campaignCheckpoints}
          height={360}
          visible={mapVisible}
          onToggle={toggleMapTeam}
          highlightedTeamId={highlightedId}
        />
      </div>

      {/* ── Leaderboard table ─────────────────────────────────────────────── */}
      {/* Header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '40px 1fr 140px 100px 120px 130px',
          gap: 8,
          padding: '8px 16px',
          background: '#fafafa',
          borderBottom: '1px solid #f0f0f0',
          fontSize: 12,
          color: '#8c8c8c',
          fontWeight: 600,
          borderRadius: '8px 8px 0 0',
        }}
      >
        <div>#</div>
        <div>Team</div>
        <div>Tiến độ</div>
        <div>Hiệu quả</div>
        <div>Thành phố</div>
        <div>Trạng thái</div>
      </div>

      {rows.map((row, idx) => {
        const isHighlighted = highlightedId === row.team_id;
        const journey       = expandedJourneys[row.team_id];
        const teamColor     = getTeamColor(idx);

        return (
          <div key={row.team_id}>
            {/* ── Row ─────────────────────────────────────────────────────── */}
            <div
              onClick={() => handleRowClick(row.team_id)}
              style={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 140px 100px 120px 130px',
                gap: 8,
                padding: '12px 16px',
                borderBottom: '1px solid #f5f5f5',
                cursor: 'pointer',
                background: isHighlighted ? '#f0f7ff' : '#fff',
                borderLeft: isHighlighted ? `3px solid ${teamColor}` : '3px solid transparent',
                transition: 'background 0.15s, border-left-color 0.15s',
                alignItems: 'center',
                opacity: mapVisible.has(row.team_id) ? 1 : 0.45,
              }}
            >
              {/* Rank */}
              <div>
                {row.rank <= 3 ? (
                  <span style={{ fontSize: 18 }}>
                    {row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : '🥉'}
                  </span>
                ) : (
                  <span style={{ fontWeight: 700, color: '#8c8c8c' }}>{row.rank}</span>
                )}
              </div>

              {/* Team name + map-toggle checkbox */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                {/* Checkbox stops propagation so it only toggles map, not expands */}
                <input
                  type="checkbox"
                  checked={mapVisible.has(row.team_id)}
                  style={{ accentColor: teamColor, width: 13, height: 13, flexShrink: 0, cursor: 'pointer' }}
                  onClick={e => e.stopPropagation()}
                  onChange={() => toggleMapTeam(row.team_id)}
                />
                <span
                  style={{ width: 8, height: 8, borderRadius: '50%', background: teamColor, flexShrink: 0 }}
                />
                {row.team_logo && (
                  <img
                    src={row.team_logo}
                    alt=""
                    style={{ width: 26, height: 26, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                  />
                )}
                <span style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.team_name}
                </span>
              </div>

              {/* Progress bar */}
              <MiniBar pct={row.completion_percent || 0} finisher={row.is_finisher_eligible} />

              {/* Effective distance */}
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {((row.effective_distance || 0) / 1000).toFixed(1)} km
              </div>

              {/* Current city */}
              <div style={{ fontSize: 13 }}>
                {row.current_city_emoji} {row.current_city}
              </div>

              {/* Eligibility */}
              <div>
                <EligibilityBadge
                  isChampion={row.is_champion_eligible}
                  isFinisher={row.is_finisher_eligible}
                />
              </div>
            </div>

            {/* ── Expanded detail (NO separate map — shared map handles it) ── */}
            {isHighlighted && (
              <div style={{ padding: '8px 16px 16px', background: '#f8faff', borderBottom: '1px solid #e8f0fe' }}>
                {journey === 'loading' || !journey ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    <Spin size="small" />
                  </div>
                ) : journey === 'error' ? (
                  <div style={{ padding: 16, color: '#cf1322', fontSize: 13 }}>
                    Không tải được chi tiết team.
                  </div>
                ) : (
                  <>
                    <RelayProgressCard journey={journey} showMap={false} />
                    {journey.member_stats?.length > 0 && (
                      <MemberStatsSection stats={journey.member_stats} />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MemberStatsSection({ stats }) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: '12px 16px',
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        marginTop: 8,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Đóng góp cá nhân</div>
      {stats.map((m, i) => (
        <div
          key={m.athlete_id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 0',
            borderBottom: i < stats.length - 1 ? '1px solid #f5f5f5' : 'none',
          }}
        >
          <div style={{ minWidth: 24, textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>
            {i + 1}
          </div>
          <div style={{ flex: 1, fontSize: 13, color: '#595959' }}>
            #{m.athlete_id}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, minWidth: 80, textAlign: 'right' }}>
            {(m.effective_distance / 1000).toFixed(1)} km
          </div>
          <div style={{ minWidth: 90 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 60, height: 4, background: '#f0f0f0', borderRadius: 2, overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${m.consistency_score}%`,
                    height: '100%',
                    background: m.consistency_score >= 80 ? '#52c41a' : m.consistency_score >= 50 ? '#faad14' : '#cf1322',
                    borderRadius: 2,
                  }}
                />
              </div>
              <span style={{ fontSize: 11, color: '#8c8c8c' }}>{m.consistency_score}%</span>
            </div>
            <div style={{ fontSize: 10, color: '#bfbfbf' }}>consistency</div>
          </div>
          <div>
            {m.min_km_met ? (
              <span style={{ fontSize: 11, color: '#52c41a' }}>✓ Min đạt</span>
            ) : (
              <span style={{ fontSize: 11, color: '#cf1322' }}>✗ Chưa đạt min</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
